import { defineStore } from 'pinia';
import ModelVersionService from '@/services/ModelVersionService';
import { CreateScanModel } from '@/domain/scan/CreateScanModel';
import SasUploadRequest from '@/domain/scan/SasUploadRequest';
import { uploadToAzureBlobStorage } from '@/utils/blobStorageUtil';
import { getVideoDurationSeconds } from '@/utils/getVideoDurationSeconds';

// How many scan videos upload at once; the rest queue. Kept low so a few big uploads don't saturate
// the connection and starve the rest of the app.
const MAX_CONCURRENT = 2;
// Auto-remove a finished-OK upload from the widget after this long so the list tidies itself.
const SUCCESS_CLEAR_MS = 6000;

// File handles are held OUTSIDE the reactive state (we never want Vue proxying a multi-GB File, nor to
// serialize it). Keyed by upload id.
const files = new Map();
let seq = 0;
let unloadBound = false;

// Native "Leave site?" guard — the only way to catch an actual tab/window close. The text is
// browser-controlled and can't be customised.
function onBeforeUnload(e) {
  e.preventDefault();
  e.returnValue = '';
}

/**
 * App-wide upload manager. Uploads run here (not in a view), so closing the scan wizard — or navigating
 * anywhere in the app — doesn't cancel them; they keep going and surface in the sidebar widget.
 */
export const useUploadStore = defineStore('uploads', {
  state: () => ({
    // { id, assetId, assetName, scanName, fileName, fileSize, progress, status, error, scanId, uploadUrl, meta }
    // status: 'queued' | 'uploading' | 'success' | 'error'
    uploads: [],
    // Bumped to make the sidebar Uploads widget flash — e.g. after a wizard hands an upload off to it.
    pulseToken: 0,
    // Bumped when the queue drains with everything succeeded (no errors left) — drives the "all
    // uploads complete" toast.
    allCompleteToken: 0,
  }),
  getters: {
    active: (s) => s.uploads.filter(u => u.status === 'queued' || u.status === 'uploading'),
    hasActive() { return this.active.length > 0; },
    runningCount: (s) => s.uploads.filter(u => u.status === 'uploading').length,
    // Overall % across the active set, for the collapsed rail indicator.
    activeProgress() {
      const a = this.active;
      if (!a.length) return 0;
      return Math.round(a.reduce((sum, u) => sum + (u.progress || 0), 0) / a.length);
    },
  },
  actions: {
    // Queue a scan upload. Returns the upload id so the caller can track this specific one.
    enqueue({ file, assetId, assetName, scanName, scanDescription, contentType,
              numberOfLenses, captureMethod, createdBy, createdBySub, organizationId }) {
      const id = `up-${Date.now()}-${++seq}`;
      files.set(id, file);
      this.uploads.push({
        id, assetId, assetName: assetName || '', scanName,
        fileName: file.name, fileSize: file.size,
        progress: 0, status: 'queued', error: null,
        scanId: null, uploadUrl: null,
        meta: { scanDescription, contentType, numberOfLenses, captureMethod, createdBy, createdBySub, organizationId },
      });
      this._armUnloadGuard();
      this._pump();
      return id;
    },

    // Start queued uploads up to the concurrency cap.
    _pump() {
      while (this.runningCount < MAX_CONCURRENT) {
        const next = this.uploads.find(u => u.status === 'queued');
        if (!next) break;
        this._run(next.id);   // fire-and-forget
      }
    },

    async _run(id) {
      const u = this.uploads.find(x => x.id === id);
      if (!u) return;
      const file = files.get(id);
      if (!file) {
        u.status = 'error';
        u.error = 'File is no longer available — please re-add it.';
        return;
      }

      u.status = 'uploading';
      u.error = null;
      try {
        const m = u.meta;
        // 1. Create the scan record once (gives us the scan id + the SAS upload URL). On a retry we keep
        //    the same URL so the re-upload resumes into the same block blob rather than orphaning a scan.
        if (!u.uploadUrl) {
          const sasReq = new SasUploadRequest(m.organizationId, u.fileName, m.contentType);
          const req = new CreateScanModel(
            u.assetId, u.scanName, m.scanDescription, m.numberOfLenses,
            m.captureMethod, m.createdBy, m.createdBySub, sasReq,
          );
          const response = await ModelVersionService.createScanRecord(req);
          u.scanId = response?.scan?.id ?? null;
          u.uploadUrl = response?.uploadUrl ?? null;
          if (!u.uploadUrl) throw new Error('No upload URL was returned.');
        }

        // 2. Upload the file straight to blob storage (block-blob for big videos; resumable on retry).
        await uploadToAzureBlobStorage(u.uploadUrl, file, u.meta.contentType, (pct) => { u.progress = pct; });

        // 3. Tell the backend the source file landed (+ video duration).
        const duration = await getVideoDurationSeconds(file).catch(() => 0);
        await ModelVersionService.updateScanSourceFileUploadStatus(u.assetId, u.scanId, true, duration);

        u.progress = 100;
        u.status = 'success';
        files.delete(id);
        setTimeout(() => this.remove(id), SUCCESS_CLEAR_MS);
      } catch (e) {
        // Keep the File so the user can retry (which resumes into the same blob). The scan record is
        // left as-is so a retry can complete it; abandoning it (remove) marks it failed.
        u.status = 'error';
        u.error = e?.message || 'Upload failed.';
      } finally {
        this._disarmIfIdle();
        this._pump();
        this._signalIfAllDone();
      }
    },

    // Flash the sidebar widget (called when a wizard hands an upload off to the background).
    pulse() {
      this.pulseToken++;
    },

    // When the queue has drained with at least one success and no outstanding errors, signal the
    // "all uploads complete" toast. (Success rows are still present here — they auto-clear a few s later.)
    _signalIfAllDone() {
      if (this.hasActive) return;
      const anySuccess = this.uploads.some(u => u.status === 'success');
      const anyError = this.uploads.some(u => u.status === 'error');
      if (anySuccess && !anyError) this.allCompleteToken++;
    },

    retry(id) {
      const u = this.uploads.find(x => x.id === id);
      if (!u || u.status !== 'error') return;
      if (!files.has(id)) { u.error = 'File is no longer available — please re-add it.'; return; }
      u.status = 'queued';
      this._armUnloadGuard();
      this._pump();
    },

    remove(id) {
      const u = this.uploads.find(x => x.id === id);
      // Abandoning a failed-but-created scan: best-effort mark it failed so it doesn't sit "uploading".
      if (u && u.status === 'error' && u.scanId) {
        ModelVersionService.updateScanSourceFileUploadStatus(u.assetId, u.scanId, false, 0).catch(() => {});
      }
      const i = this.uploads.findIndex(x => x.id === id);
      if (i !== -1) this.uploads.splice(i, 1);
      files.delete(id);
      this._disarmIfIdle();
    },

    _armUnloadGuard() {
      if (!unloadBound) { window.addEventListener('beforeunload', onBeforeUnload); unloadBound = true; }
    },
    _disarmIfIdle() {
      if (!this.hasActive && unloadBound) { window.removeEventListener('beforeunload', onBeforeUnload); unloadBound = false; }
    },
  },
});
